import { useI18n } from 'vue-i18n'
import usePost from '@/core/composables/api/usePost'
import { useSnackStore } from '@/stores/snack.store'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useUserStore } from '@/stores/user.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { storeToRefs } from 'pinia'

export const useStartSelfFeedback = (callback?: () => void) => {
  const SELF_FEEDBACK_STEPS = {
    selectTemplates: 'select-templates',
    selectQuestionGroups: 'select-question-groups',
    addMetadata: 'add-metadata',
  } as const
  type SelfFeedbackStep = (typeof SELF_FEEDBACK_STEPS)[keyof typeof SELF_FEEDBACK_STEPS]

  const snackStore = useSnackStore()
  const { t } = useI18n()
  const { user } = useUserStore()
  const bus = useEventBus(EventKey.surveyUpdated)

  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  const selfFeedbackFormData = reactive<SelfFeedbackFormData>({
    title: undefined,
    message: undefined,
    templateIds: [],
    questionGroupIds: [],
  })

  const filteredTemplates = computed(() =>
    templates.value.filter((template) => template.includeSelf),
  )

  const skipTemplateSelection = computed(() => filteredTemplates.value.length === 1)

  const autoSelectSingleTemplate = () => {
    if (skipTemplateSelection.value && selfFeedbackFormData.templateIds.length === 0) {
      selfFeedbackFormData.templateIds = [filteredTemplates.value[0].id]
    }
  }

  watch(filteredTemplates, autoSelectSingleTemplate)

  onMounted(() => {
    loadTemplates()
  })

  const errors = ref<ErrorMap>(new Map())

  const validateStep = () => {
    errors.value.clear()

    switch (step.value) {
      case SELF_FEEDBACK_STEPS.selectTemplates:
        if (selfFeedbackFormData.templateIds.length === 0) {
          errors.value.set('templateIds', t('feedback.formValidation.templates'))
        }
        break
      case SELF_FEEDBACK_STEPS.selectQuestionGroups:
        if (
          selfFeedbackFormData.questionGroupIds.length === 0 &&
          !configurableTemplates.value.some((template) =>
            template.questionGroupSections?.some((section) => section.required),
          )
        ) {
          errors.value.set('questionGroupIds', t('feedback.formValidation.questionGroups'))
        }
        break
    }
  }

  const {
    mutate,
    status,
    data: createdSurveyId,
  } = usePost<string, SelfFeedbackFormData>('survey/self')

  const router = useRouter()

  const startSelfFeedback = async () => {
    validateStep()
    if (errors.value.size == 0) {
      await mutate(selfFeedbackFormData)
    }

    switch (status.value) {
      case ApiRequestStatus.Success:
        bus.emit({ surveyId: createdSurveyId.value })
        if (callback) {
          callback()
        }
        router.push({
          name: 'feedback',
          params: { surveyId: createdSurveyId.value, receiverId: user.id },
        })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('self-feedback.error'))
        break
    }
  }

  const configurableTemplates = computed(() => {
    return templates.value
      .filter((template) => selfFeedbackFormData.templateIds.includes(template.id))
      .filter(
        (template) =>
          template.questionGroupSections &&
          template.questionGroupSections.filter((section) => !section.required).length > 0,
      )
  })

  const isConfigurable = computed(() => configurableTemplates.value.length > 0)

  const getTemplateQuestionGroupIds = (templateId: string) => {
    return (
      templates.value
        .find((template) => template.id === templateId)
        ?.questionGroupSections?.flatMap((section) => section.questionGroups)
        .map((questionGroup) => questionGroup.id) ?? []
    )
  }

  watch(
    () => [...selfFeedbackFormData.templateIds],
    (templateIds, previousTemplateIds = []) => {
      const addedTemplateIds = templateIds.filter((id) => !previousTemplateIds.includes(id))
      const removedTemplateIds = previousTemplateIds.filter((id) => !templateIds.includes(id))

      const addedQuestionGroupIds = addedTemplateIds.flatMap((templateId) =>
        getTemplateQuestionGroupIds(templateId),
      )
      selfFeedbackFormData.questionGroupIds = Array.from(
        new Set([...selfFeedbackFormData.questionGroupIds, ...addedQuestionGroupIds]),
      )

      const removedQuestionGroupIds = new Set(
        removedTemplateIds.flatMap((templateId) => getTemplateQuestionGroupIds(templateId)),
      )
      selfFeedbackFormData.questionGroupIds = selfFeedbackFormData.questionGroupIds.filter(
        (id) => !removedQuestionGroupIds.has(id),
      )
    },
  )

  const steps = computed<SelfFeedbackStep[]>(() => [
    ...(skipTemplateSelection.value ? [] : [SELF_FEEDBACK_STEPS.selectTemplates]),
    ...(isConfigurable.value ? [SELF_FEEDBACK_STEPS.selectQuestionGroups] : []),
    SELF_FEEDBACK_STEPS.addMetadata,
  ])

  const stepIndex = ref(0)
  const step = computed(() => steps.value[stepIndex.value])
  const isFirstStep = computed(() => stepIndex.value === 0)
  const isLastStep = computed(() => stepIndex.value === steps.value.length - 1)

  watch(steps, (newSteps) => {
    if (stepIndex.value >= newSteps.length) {
      stepIndex.value = Math.max(0, newSteps.length - 1)
    }
  })

  const nextStep = () => {
    validateStep()
    if (errors.value.size === 0 && !isLastStep.value) {
      stepIndex.value++
    }
  }

  const previousStep = () => {
    if (stepIndex.value > 0) {
      stepIndex.value--
      errors.value.clear()
    }
  }

  watch(selfFeedbackFormData, () => {
    errors.value.clear()
  })

  const clearForm = () => {
    errors.value.clear()
    selfFeedbackFormData.title = undefined
    selfFeedbackFormData.message = undefined
    selfFeedbackFormData.templateIds = []
    selfFeedbackFormData.questionGroupIds = []
    stepIndex.value = 0
  }

  return {
    step,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    templates: filteredTemplates,
    configurableTemplates,
    loadingStatus,
    errors,
    selfFeedbackFormData,
    clearForm,
    startSelfFeedback,
    autoSelectSingleTemplate,
  }
}

type SelfFeedbackFormData = {
  title?: string
  message?: JSON
  templateIds: string[]
  questionGroupIds: string[]
  taskId?: string
}

type ErrorMap = Map<keyof SelfFeedbackFormData, string>
